import { Seo } from '~/components/Seo'
import { easeInOut, motion } from 'motion/react'
import { Calendar, Coffee, Eye, Sparkles, Users } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { PageLayout } from '~/lib/components/layouts/PageLayout'

/**
 * The LinkedIn mark, inlined.
 *
 * Inlined because no icon package we can use still ships it, checked on
 * 2026-08-21: lucide-react 1.31.0 dropped brand glyphs and exports no
 * `Linkedin`; @icons-pack/react-simple-icons 13.15.1, whose whole purpose is
 * official brand marks, exports nothing matching LinkedIn since simple-icons
 * removed it over trademark. react-icons 5.7.0 does have `FaLinkedin`, but that
 * is Font Awesome's own redrawing, so it costs a dependency and returns a less
 * faithful glyph than the path below.
 *
 * A generic link icon would lose the recognition that makes the button work at a
 * glance, which is the whole point of putting it there.
 */
const LinkedInIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05a3.74 3.74 0 0 1 3.37-1.85c3.6 0 4.27 2.37 4.27 5.46v6.28ZM5.34 7.43a2.07 2.07 0 1 1 0-4.13 2.07 2.07 0 0 1 0 4.13Zm1.78 13.02H3.55V9h3.57v11.45ZM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0Z" />
  </svg>
)

/**
 * The two founders, named in full and linked to their real profiles.
 *
 * A reviewing client told us "Hatem" and "François" read like the names of an
 * AI, with no humans behind them. Surnames, faces and LinkedIn are what turn a
 * first name into somebody you can check, follow and hold responsible. The
 * LinkedIn URLs are also what let a search engine tie these two pages together,
 * so they are repeated as sameAs in the page structured data.
 */
const founders = [
  {
    name: 'Hatem Rabeh',
    role: 'Co-founder, clinical evaluation expert',
    bio: 'Medical doctor, medical engineer and clinical evaluation specialist. Writes the CERs and PMCFs that the product has to survive.',
    photo: '/team/hatem-rabeh.webp',
    linkedin: 'https://www.linkedin.com/in/hatem-rabeh-clinical-evaluation-medical-device',
    linkedinLabel: 'Hatem on LinkedIn',
  },
  {
    name: 'François Girinon',
    role: 'Co-founder, technical expert',
    bio: 'Builds SOTA Navigator. Turns a regulatory requirement into something that runs, and keeps it fast enough to use on a deadline.',
    photo: '/team/francois-girinon.webp',
    linkedin: 'https://www.linkedin.com/in/fran%C3%A7ois-girinon/',
    linkedinLabel: 'François on LinkedIn',
  },
]

const floatingAnimation = {
  initial: { y: 0 },
  animate: {
    y: [-10, 10, -10],
    transition: {
      duration: 6,
      repeat: Infinity,
      ease: easeInOut,
    },
  },
}

const glowAnimation = {
  initial: { opacity: 0.3 },
  animate: {
    opacity: [0.3, 0.6, 0.3],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: easeInOut,
    },
  },
}

export default function OurStory() {
  return (
    <PageLayout>
      <Seo
        title="Our Story | SOTA Navigator"
        description="Who builds SOTA Navigator: Hatem Rabeh, clinical evaluation expert, and François Girinon, engineer. Why two people built a literature review tool for medical device clinical evaluations."
        jsonLd={founders.map((founder) => ({
          '@context': 'https://schema.org',
          '@type': 'Person',
          name: founder.name,
          jobTitle: founder.role,
          description: founder.bio,
          // sameAs is what lets a search engine tie the name on this page to the
          // real profile, which is the machine-readable half of the client's
          // complaint that these read like invented people.
          sameAs: [founder.linkedin],
          worksFor: { '@type': 'Organization', name: 'SOTA Navigator' },
        }))}
      />

      <div className="relative overflow-hidden bg-[#001871] text-white">
        {/* Enhanced texture overlay */}
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1584036561566-baf8f5f1b144?ixlib=rb-4.0.3')] opacity-5 mix-blend-soft-light"></div>
        <div className="absolute inset-0 bg-linear-to-b from-[#001871] via-[#001871] to-[#001871]/90"></div>

        {/* Abstract shapes */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 0.15, scale: 1 }}
          transition={{ duration: 2 }}
          className="absolute left-20 top-20 size-96 rounded-full bg-white blur-3xl"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 0.15, scale: 1 }}
          transition={{ duration: 2, delay: 0.5 }}
          className="absolute bottom-20 right-20 size-96 rounded-full bg-white blur-3xl"
        />

        {/* Floating elements */}
        <motion.div
          variants={floatingAnimation}
          initial="initial"
          animate="animate"
          className="absolute left-20 top-40 hidden size-20 rounded-2xl bg-white/20 shadow-lg backdrop-blur-lg lg:block"
        />
        <motion.div
          variants={floatingAnimation}
          initial="initial"
          animate="animate"
          transition={{ delay: 1 }}
          className="absolute bottom-40 right-20 hidden size-16 rounded-full bg-white/20 shadow-lg backdrop-blur-lg lg:block"
        />

        <div className="relative py-32">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-4xl">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
                className="space-y-8 text-center"
              >
                <motion.div
                  variants={glowAnimation}
                  initial="initial"
                  animate="animate"
                  className="inline-flex items-center rounded-full bg-white/20 px-6 py-2 text-sm font-medium text-white shadow-lg backdrop-blur-xs"
                >
                  Our Journey
                </motion.div>

                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
                  className="text-4xl font-bold tracking-tight text-white [text-shadow:0_1px_2px_rgb(0_0_0/20%)] sm:text-7xl"
                >
                  We didn't build a tool.
                  <br />
                  We built something we wished existed years ago.
                </motion.h1>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' }}
                  className="text-xl leading-8 text-white [text-shadow:0_1px_2px_rgb(0_0_0/20%)] sm:text-2xl"
                >
                  Hey, thanks for being here. We're Hatem and François — and if you've ever stayed
                  up late drowning in PubMed tabs, Excel filters, and last-minute formatting before
                  a deadline... Then yeah, this is for you.
                </motion.p>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Wave transition */}
        <div className="absolute bottom-0 left-0 w-full rotate-180 overflow-hidden leading-none">
          <svg
            className="relative block h-[50px] w-full"
            data-name="Layer 1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
          >
            <path
              d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
              className="fill-white"
            ></path>
          </svg>
        </div>
      </div>

      {/* Why we built section */}
      <section className="relative bg-white py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16 text-center"
          >
            <h2 className="mb-4 text-4xl font-bold text-[#001871] md:text-5xl">
              Why we built SOTA Navigator
            </h2>
            <p className="text-xl text-gray-600">
              We started with a simple, painful question that needed answering.
            </p>
          </motion.div>

          <div className="mx-auto max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="rounded-xl border border-[#001871]/10 bg-white p-8 shadow-lg"
            >
              <p className="mb-6 text-lg text-gray-600">
                Let me be honest. We didn't start with some grand startup idea. We started with a
                simple, painful question:
              </p>

              <blockquote className="mb-6 border-l-4 border-[#001871] bg-[#001871]/5 p-6 text-lg italic text-gray-700">
                Why is literature review still this exhausting?
                <br />
                Why are we wasting so many hours on something that should already be easier?
              </blockquote>

              <p className="text-lg text-gray-600">
                Hatem has written more CERs and PMCFs than he can count, and every single time the
                most frustrating part was the same: the literature review. François had spent years
                building software for people whose real problem was never the software. So we built
                the thing we kept wishing existed.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The two of us */}
      <section className="relative bg-gray-50 py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16 text-center"
          >
            <h2 className="mb-4 text-4xl font-bold text-[#001871] md:text-5xl">The two of us</h2>
            <p className="text-xl text-gray-600">
              Two people, both reachable. Here is where to find us.
            </p>
          </motion.div>

          <div className="mx-auto grid max-w-4xl grid-cols-1 gap-8 md:grid-cols-2">
            {founders.map((founder, index) => (
              <motion.div
                key={founder.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full border border-gray-100 bg-white transition-all duration-300 hover:shadow-xl">
                  <CardContent className="flex h-full flex-col items-center p-8 text-center">
                    <img
                      src={founder.photo}
                      alt={`Portrait of ${founder.name}`}
                      width={160}
                      height={160}
                      loading="lazy"
                      className="mb-6 size-40 rounded-full object-cover shadow-md"
                    />
                    <h3 className="text-2xl font-semibold text-[#001871]">{founder.name}</h3>
                    <p className="mt-1 text-sm font-medium uppercase tracking-wide text-[#001871]/70">
                      {founder.role}
                    </p>
                    <p className="mt-4 grow text-gray-600">{founder.bio}</p>
                    <a
                      href={founder.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-6 inline-flex items-center gap-2 rounded-lg border border-[#001871] px-4 py-2 font-medium text-[#001871] transition-colors hover:bg-[#001871] hover:text-white"
                    >
                      <LinkedInIcon className="size-5" />
                      {founder.linkedinLabel}
                    </a>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who this is for section */}
      <section className="relative overflow-hidden bg-[#001871] py-24 text-white">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1584036561566-baf8f5f1b144?ixlib=rb-4.0.3')] opacity-5 mix-blend-soft-light"></div>
        <div className="absolute inset-0 bg-linear-to-b from-[#001871] via-[#001871] to-[#001871]/90"></div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16 text-center"
          >
            <h2 className="mb-4 text-4xl font-bold tracking-tight text-white [text-shadow:0_1px_2px_rgb(0_0_0/20%)] sm:text-5xl">
              Who this is for
            </h2>
            <p className="text-xl text-white/90">
              For the clinical and regulatory teams who carry the literature review inside a
              clinical evaluation.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mx-auto max-w-3xl rounded-xl border border-white/20 bg-white/10 p-8 backdrop-blur-xs"
          >
            <p className="mb-6 text-lg text-white/90">
              Running the searches, screening the hits on title and abstract, appraising the full
              texts, justifying every inclusion and exclusion, and keeping the PRISMA counts
              reconcilable from the first query to the final table. It is skilled work, and most of
              the hours go to steps that are mechanical rather than scientific.
            </p>
            <p className="text-xl font-semibold text-white">
              Those are the hours we set out to give back.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What we care about section */}
      <section className="relative bg-white py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16 text-center"
          >
            <h2 className="mb-4 text-4xl font-bold text-[#001871] md:text-5xl">
              What we care about
            </h2>
            <p className="text-xl text-gray-600">
              Building tools that actually make your life easier.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Card className="border border-gray-100 bg-white transition-all duration-300 hover:shadow-xl">
                <CardContent className="p-6">
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-4 rounded-xl bg-[#001871]/5 p-4">
                      <Eye className="size-10 text-[#001871]" />
                    </div>
                    <h3 className="mb-2 text-xl font-semibold text-[#001871]">Clarity First</h3>
                    <p className="text-gray-600">
                      We believe in making complex processes clear and simple.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <Card className="border border-gray-100 bg-white transition-all duration-300 hover:shadow-xl">
                <CardContent className="p-6">
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-4 rounded-xl bg-[#001871]/5 p-4">
                      <Users className="size-10 text-[#001871]" />
                    </div>
                    <h3 className="mb-2 text-xl font-semibold text-[#001871]">User-Driven</h3>
                    <p className="text-gray-600">
                      Built from real feedback, real reviews, real people.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Card className="border border-gray-100 bg-white transition-all duration-300 hover:shadow-xl">
                <CardContent className="p-6">
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-4 rounded-xl bg-[#001871]/5 p-4">
                      <Sparkles className="size-10 text-[#001871]" />
                    </div>
                    <h3 className="mb-2 text-xl font-semibold text-[#001871]">Simplicity</h3>
                    <p className="text-gray-600">
                      No unnecessary features, just what you need to work better.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The future section */}
      <section className="relative overflow-hidden bg-[#001871] py-24 text-white">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1584036561566-baf8f5f1b144?ixlib=rb-4.0.3')] opacity-5 mix-blend-soft-light"></div>
        <div className="absolute inset-0 bg-linear-to-b from-[#001871] via-[#001871] to-[#001871]/90"></div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16 text-center"
          >
            <h2 className="mb-4 text-4xl font-bold tracking-tight text-white [text-shadow:0_1px_2px_rgb(0_0_0/20%)] sm:text-5xl">
              Where this goes
            </h2>
            <p className="text-xl text-white/90">
              Building the future of literature review, together.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mx-auto max-w-3xl rounded-xl border border-white/20 bg-white/10 p-8 text-center backdrop-blur-xs"
          >
            <p className="mb-6 text-lg text-white/90">
              We build it in the open, with the people who use it. Every question, bug report and
              feature request from a working reviewer goes on the roadmap, and the roadmap is public
              so you can see where yours sits.
            </p>
            <p className="text-xl font-semibold text-white">
              You can see what we are building, and vote on what comes next.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact section */}
      <section className="relative bg-white py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16 text-center"
          >
            <h2 className="mb-4 text-4xl font-bold text-[#001871] md:text-5xl">
              Let's Make Literature Reviews Better Together
            </h2>
            <p className="mx-auto max-w-2xl text-xl text-gray-600">
              Every conversation helps us understand your challenges better and shapes the future of
              SOTA Navigator.
            </p>
          </motion.div>

          <div className="mx-auto max-w-3xl">
            <div className="mb-16 grid grid-cols-1 gap-8 md:grid-cols-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="rounded-xl border border-[#001871]/10 bg-white p-8 shadow-lg transition-all duration-300 hover:shadow-xl"
              >
                <div className="mb-4 flex items-center justify-center">
                  <div className="rounded-full bg-[#001871]/5 p-4">
                    <Coffee className="size-8 text-[#001871]" />
                  </div>
                </div>
                <h3 className="mb-3 text-center text-xl font-semibold text-[#001871]">
                  Chat with Us
                </h3>
                <p className="mb-4 text-center text-gray-600">
                  Share ideas, ask questions, or just say hi. We read and respond to every message.
                </p>
                <div className="flex justify-center">
                  <a
                    href="mailto:admin@sotanavigator.com"
                    className="inline-flex items-center gap-2 rounded-full bg-[#001871]/5 px-6 py-3 text-[#001871] transition-colors hover:bg-[#001871]/10"
                  >
                    admin@sotanavigator.com
                  </a>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="rounded-xl border border-[#001871]/10 bg-white p-8 shadow-lg transition-all duration-300 hover:shadow-xl"
              >
                <div className="mb-4 flex items-center justify-center">
                  <div className="rounded-full bg-[#001871]/5 p-4">
                    <Calendar className="size-8 text-[#001871]" />
                  </div>
                </div>
                <h3 className="mb-3 text-center text-xl font-semibold text-[#001871]">
                  Book a Demo
                </h3>
                <p className="mb-4 text-center text-gray-600">
                  See how SOTA Navigator can transform your literature review process.
                </p>
                <div className="flex justify-center">
                  <a
                    href="https://calendly.com/hatem-rabeh/30-min-seesion-with-dr-hatem-rabeh"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-full bg-[#001871] px-6 py-3 text-white transition-colors hover:bg-[#001871]/90"
                  >
                    Schedule Now
                  </a>
                </div>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="relative rounded-2xl bg-[#001871] p-8 text-center text-white md:p-12"
            >
              {/* Abstract shapes */}
              <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1584036561566-baf8f5f1b144?ixlib=rb-4.0.3')] opacity-5 mix-blend-soft-light"></div>
              <div className="absolute inset-0 bg-linear-to-b from-[#001871] via-[#001871] to-[#001871]/90"></div>

              <div className="relative space-y-6">
                <p className="text-xl font-medium text-white/90">
                  Tell us what your review actually costs you.
                </p>
                <div className="space-y-2 text-white/80">
                  <p>
                    Send us the step that takes the longest on your current clinical evaluation.
                  </p>
                  <p>
                    We will tell you plainly whether SOTA Navigator shortens it, and how, or whether
                    it does not.
                  </p>
                </div>
                <div className="mx-auto mt-8 h-px w-24 bg-white/20"></div>
                <p className="font-medium text-white">
                  <span className="mt-2 block text-lg">Hatem Rabeh and François Girinon</span>
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </PageLayout>
  )
}
